'use client';

// #region IMPORT
import { useState } from 'react';
import { m } from 'framer-motion';

import { Box, Grid, Stack, Button, Collapse, Typography } from '@mui/material';

import { paths } from 'src/routes/paths';

import { useResponsive } from 'src/hooks/use-responsive';

import { BRAND_COLOR } from 'src/theme/core';

import { CenterRegisterForm } from './center-register-form';
// #endregion

const ADVANTAGES = [
  {
    title: 'E-Learning & A.I',
    subTitle: 'Tạo câu hỏi & bài tập',
    description:
      'Tạo nhiều loại câu hỏi, bài tập & đề thi với sự trợ giúp của AI - Tổ chức câu hỏi theo trình độ, chủ đề - Xây dựng ngân hàng riêng hoặc sử dụng ngân hàng chuẩn hóa từ CenterUp.',
    image: '/assets/sub-pages/lms/generate-assignment/overview.png',
    url: paths.subPage.lms.generateAssigment,
  },
  {
    title: 'E-Learning & A.I',
    subTitle: 'Giao & chấm bài tự động',
    description:
      'Giao bài linh hoạt theo lớp, nhóm hoặc từng học viên - Cài đặt thời gian làm bài và thời hạn nộp - Giới hạn số lần làm bài và điểm tối thiểu - Chấm điểm tự động với AI.',
    image: '/assets/sub-pages/lms/assign-grade-auto/intro.png',
    url: paths.subPage.lms.assignGradeAuto,
  },
  {
    title: 'E-Learning & A.I',
    subTitle: 'Trắc nghiệm online',
    description:
      'Học sinh làm bài trắc nghiệm trực tuyến - Giao diện làm bài đơn giản, rõ ràng - Thông báo nhắc nhở tự động - Hiển thị kết quả ngay sau khi làm bài.',
    image: '/assets/sub-pages/lms/quiz-online/intro.png',
    url: paths.subPage.lms.quizOnline,
  },
];

type Advantage = (typeof ADVANTAGES)[number];

export const TeachingFeature = () => {
  const smUp = useResponsive('up', 'sm');
  const lgUp = useResponsive('up', 'lg');
  const [openDemo, setOpenDemo] = useState<boolean>(false);
  const [selectedAdvantage, setSelectedAdvantage] = useState<Advantage>(ADVANTAGES[0]);

  const handleSelect = (advantage: Advantage) => {
    setSelectedAdvantage(advantage);
  };

  const renderTabletImage = (advantage: Advantage) => (
    <Stack
      alignItems="center"
      justifyContent="center"
      sx={{
        position: 'relative',
        isolation: 'isolate',
        width: 1,
        minHeight: {
          sm: 320,
          md: 430,
        },
        mt: 2,
        overflow: 'visible',

        '&::before': {
          content: '""',
          position: 'absolute',
          zIndex: 0,
          width: {
            sm: '78%',
            md: '72%',
          },
          height: '42%',
          left: '14%',
          bottom: '-4%',
          borderRadius: '50%',
          background: `
            radial-gradient(
              circle at 28% 62%,
              rgba(105, 92, 255, 0.08) 0%,
              rgba(153, 100, 255, 0.04) 38%,
              transparent 72%
            ),
            radial-gradient(
              circle at 72% 64%,
              rgba(255, 129, 204, 0.07) 0%,
              rgba(255, 178, 221, 0.035) 38%,
              transparent 72%
            ),
            radial-gradient(
              circle at 56% 82%,
              rgba(255, 221, 120, 0.05) 0%,
              transparent 68%
            )
          `,
          filter: 'blur(42px)',
          pointerEvents: 'none',
        },
      }}
    >
      <Box
        component={m.img}
        key={advantage.image}
        src={advantage.image}
        alt={advantage.subTitle}
        initial={{ opacity: 0, scale: 0.96 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{
          duration: 0.4,
          ease: 'easeInOut',
        }}
        sx={{
          position: 'relative',
          zIndex: 1,
          display: 'block',
          width: {
            sm: '100%',
            md: '94%',
          },
          maxWidth: 820,
          height: 'auto',
          maxHeight: {
            sm: 400,
            md: 520,
          },
          objectFit: 'contain',
          objectPosition: 'center',
          userSelect: 'none',
          pointerEvents: 'none',
          filter: 'drop-shadow(0 14px 24px rgba(77, 59, 163, 0.07))',
        }}
      />
    </Stack>
  );

  const renderStack = (advantage: Advantage) => {
    const isSelected = selectedAdvantage.subTitle === advantage.subTitle;

    return (
      <Stack
        spacing={1}
        onClick={() => handleSelect(advantage)}
        sx={{
          width: 1,
          cursor: 'pointer',
        }}
      >
        <Typography
          variant="h3"
          fontWeight="bold"
          color={isSelected ? undefined : 'text.disabled'}
          sx={
            isSelected
              ? {
                  width: 'fit-content',
                  background: BRAND_COLOR,
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                  color: 'transparent',
                }
              : undefined
          }
        >
          {advantage.subTitle}
        </Typography>

        <Collapse in={isSelected} timeout="auto">
          <Stack spacing={2}>
            <Typography
              component="div"
              sx={{
                width: 1,
                maxWidth: {
                  sm: 'none',
                  lg: 520,
                },
                lineHeight: 1.6,
              }}
            >
              {advantage.description}
            </Typography>

            <Stack direction="row" alignItems="center" flexWrap="wrap" gap={1}>
              <Button
                size="large"
                href={advantage.url}
                variant="contained"
                onClick={(event) => event.stopPropagation()}
                sx={{
                  bgcolor: '#202733',
                  boxShadow: 'none',

                  '&:hover': {
                    bgcolor: '#171D26',
                    boxShadow: 'none',
                  },
                }}
              >
                Xem chi tiết
              </Button>

              <Button
                size="large"
                color="primary"
                variant="contained"
                onClick={() => setOpenDemo(true)}
              >
                Đăng ký Demo
              </Button>
            </Stack>

            {/* Tablet: ảnh hiển thị ngay dưới mục đang chọn */}
            {!lgUp && renderTabletImage(advantage)}
          </Stack>
        </Collapse>
      </Stack>
    );
  };

  const renderStackMobile = (advantage: Advantage, index: number) => {
    const isSelected = selectedAdvantage.subTitle === advantage.subTitle;

    return (
      <Stack
        spacing={1}
        onClick={() => handleSelect(advantage)}
        sx={{
          width: 1,
          cursor: 'pointer',
        }}
      >
        <Typography
          variant="h4"
          fontWeight="bold"
          color={isSelected ? undefined : 'text.disabled'}
          sx={
            isSelected
              ? {
                  width: 'fit-content',
                  background: BRAND_COLOR,
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                  color: 'transparent',
                }
              : undefined
          }
        >
          {advantage.subTitle}
        </Typography>

        <Collapse in={isSelected} timeout="auto" unmountOnExit>
          <Stack spacing={2} pt={1}>
            <Typography
              component="div"
              sx={{
                width: 1,
                lineHeight: 1.6,
                fontSize: 14,
              }}
            >
              {advantage.description}
            </Typography>

            <Stack direction="row" alignItems="center" flexWrap="wrap" gap={1}>
              <Button
                size="medium"
                href={advantage.url}
                variant="contained"
                onClick={(event) => event.stopPropagation()}
                sx={{
                  bgcolor: '#202733',
                  boxShadow: 'none',
                  '&:hover': {
                    bgcolor: '#171D26',
                    boxShadow: 'none',
                  },
                }}
              >
                Xem chi tiết
              </Button>

              <Button
                size="medium"
                color="primary"
                variant="contained"
                onClick={() => setOpenDemo(true)}
              >
                Đăng ký Demo
              </Button>
            </Stack>

            <Box
              component="img"
              src={advantage.image}
              alt={advantage.subTitle}
              sx={{
                display: 'block',
                width: 1,
                height: 'auto',
                maxHeight: 320,
                objectFit: 'contain',
                objectPosition: 'center',
              }}
            />
          </Stack>
        </Collapse>
      </Stack>
    );
  };

  return (
    <Stack py={{ xs: 3, sm: 4, md: 5 }} spacing={smUp ? 0 : 2.5}>
      {smUp ? (
        <>
          <Stack spacing={2} alignItems="center">
            <Typography
              variant="subtitle1"
              fontFamily='"Shantell Sans Light", sans-serif'
              fontWeight="bold"
              color="primary.main"
            >
              Blended learning | Student-Centric
            </Typography>

            <Stack direction="row" spacing={1.2} justifyContent="center" alignItems="center">
              <Typography variant="h2" fontWeight="bold">
                Giảng dạy hiệu quả
              </Typography>
            </Stack>

            <Typography
              textAlign="center"
              px={{ sm: 4, md: 10, lg: 15 }}
              mt={2.5}
              mb={5}
              fontWeight="light"
              maxWidth={746}
            >
              Tạo các bài kiểm tra từ kho đề đa dạng và các bộ giáo trình nổi tiếng. Tổ chức kiểm
              tra online dễ dàng, giao & chấm tự động với AI
            </Typography>
          </Stack>

          <Grid
            container
            spacing={{
              sm: 4,
              lg: 6,
            }}
          >
            <Grid item xs={12} lg={4.5}>
              <Stack
                height={1}
                justifyContent="center"
                spacing={{
                  sm: 3,
                  lg: 8,
                }}
              >
                <Grid
                  container
                  spacing={{
                    sm: 3,
                    lg: 3,
                  }}
                >
                  {ADVANTAGES.map((item) => (
                    <Grid item key={item.subTitle} xs={12}>
                      {renderStack(item)}
                    </Grid>
                  ))}
                </Grid>
              </Stack>
            </Grid>

            {/* Desktop: ảnh lớn nằm bên phải */}
            {lgUp && (
              <Grid item lg={7.5}>
                <Stack
                  width={1}
                  height={1}
                  justifyContent="center"
                  sx={{
                    position: 'relative',
                    isolation: 'isolate',

                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      zIndex: 0,
                      width: '76%',
                      height: '54%',
                      left: '20%',
                      bottom: '-10%',
                      borderRadius: '50%',
                      background: `
                        radial-gradient(
                          circle at 28% 60%,
                          rgba(105, 92, 255, 0.08) 0%,
                          rgba(153, 100, 255, 0.04) 36%,
                          transparent 70%
                        ),
                        radial-gradient(
                          circle at 72% 62%,
                          rgba(255, 129, 204, 0.07) 0%,
                          rgba(255, 178, 221, 0.035) 36%,
                          transparent 70%
                        ),
                        radial-gradient(
                          circle at 56% 82%,
                          rgba(255, 221, 120, 0.05) 0%,
                          transparent 66%
                        )
                      `,
                      filter: 'blur(46px)',
                      pointerEvents: 'none',
                    },

                    '&::after': {
                      content: '""',
                      position: 'absolute',
                      zIndex: 0,
                      width: '38%',
                      aspectRatio: '1',
                      right: '-4%',
                      bottom: '-12%',
                      borderRadius: '50%',
                      background: `
                        radial-gradient(
                          circle,
                          rgba(126, 220, 255, 0.06) 0%,
                          rgba(132, 155, 255, 0.03) 44%,
                          transparent 74%
                        )
                      `,
                      filter: 'blur(42px)',
                      pointerEvents: 'none',
                    },
                  }}
                >
                  <Box
                    component={m.div}
                    key={selectedAdvantage.image}
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.9 }}
                    transition={{
                      duration: 0.5,
                      ease: 'easeInOut',
                    }}
                    sx={{
                      position: 'relative',
                      zIndex: 1,
                      width: 1,
                      aspectRatio: '4 / 3',
                      backgroundImage: `url(${selectedAdvantage.image})`,
                      backgroundSize: 'contain',
                      backgroundRepeat: 'no-repeat',
                      backgroundPosition: 'right center',
                      filter: 'drop-shadow(0 14px 24px rgba(77, 59, 163, 0.07))',
                    }}
                  />
                </Stack>
              </Grid>
            )}
          </Grid>
        </>
      ) : (
        <Stack>
          <Typography
            textAlign="center"
            variant="body2"
            fontFamily='"Shantell Sans Light", sans-serif'
            fontWeight="bold"
            color="primary.main"
          >
            Blended learning | Student-Centric
          </Typography>

          <Stack justifyContent="center" alignItems="center" my={1}>
            <Typography variant="h2" fontWeight="bold">
              Giảng dạy hiệu quả
            </Typography>
          </Stack>

          <Typography textAlign="center" mt={1} mb={2} fontWeight="light">
            Tạo các bài kiểm tra từ kho đề đa dạng và các bộ giáo trình nổi tiếng. Tổ chức kiểm tra
            online dễ dàng, giao & chấm tự động với AI
          </Typography>

          <Stack spacing={3} my={2.5}>
            {ADVANTAGES.map((item, index) => (
              <Box key={item.subTitle}>{renderStackMobile(item, index)}</Box>
            ))}
          </Stack>
        </Stack>
      )}
      {openDemo && <CenterRegisterForm open={openDemo} onClose={() => setOpenDemo(false)} />}
    </Stack>
  );
};
